/**
 * AI Enums and Conversion Utilities
 * 
 * DEBUG LOGGING:
 * To enable/disable conversion debug logs:
 * - In browser console: window.toggleConversionDebug() or window.toggleConversionDebug(true/false)
 * - In code: import { toggleConversionDebug } from './ai/enums.js' and call toggleConversionDebug()
 * - Check status: window.isConversionDebugEnabled() or import { isConversionDebugEnabled }
 */

// Debug flag for toggling conversion logs
let DEBUG_CONVERSIONS = false;

// Function to toggle debug logging
export const toggleConversionDebug = (enabled = null) => {
    if (enabled === null) {
        DEBUG_CONVERSIONS = !DEBUG_CONVERSIONS;
    } else {
        DEBUG_CONVERSIONS = enabled;
    }
    console.log(`[CONVERSION_DEBUG] Debug logging is now ${DEBUG_CONVERSIONS ? 'ENABLED' : 'DISABLED'}`);
    return DEBUG_CONVERSIONS;
};

// Function to check if debug logging is enabled
export const isConversionDebugEnabled = () => DEBUG_CONVERSIONS;

// Global function for browser console access (development only)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    window.toggleConversionDebug = toggleConversionDebug;
    window.isConversionDebugEnabled = isConversionDebugEnabled;
}

// Enum definitions for the clean database schema

// Birth Year Range Enum (0-5)
export const BIRTH_YEAR_RANGES = {
    0: "under20",
    1: "20-30", 
    2: "31-40",
    3: "41-50",
    4: "51-60",
    5: "over60"
};

// Education Level Enum (0-3)
export const EDUCATION_LEVELS = {
    0: "high_school",
    1: "bachelors", 
    2: "masters",
    3: "doctorate"
};

// Language Enum (0-3)
export const LANGUAGES = {
    0: "en",
    1: "zh-CN",
    2: "zh-Hant",
    3: "ja"
};

// Full language names for prompts
export const LANGUAGE_NAMES = {
    "en": "English",
    "zh-CN": "Simplified Chinese",
    "zh-Hant": "Traditional Chinese",
    "ja": "Japanese"
};

// Learning Purpose Enum (0-1)
export const LEARNING_PURPOSES = {
    0: "hobby",
    1: "professional"
};

// Knowledge Status Enum (0-5)
export const KNOWLEDGE_STATUSES = {
    0: "easy",
    1: "known",
    2: "partial", 
    3: "new",
    4: "hard",
    5: "skip"
};

// Role Enum (0-1)
export const ROLES = {
    0: "user",
    1: "bot"
};

// Tristate Enum (0-2)
export const TRISTATE_STATES = {
    0: "tip-only",
    1: "full",
    2: "chat-only"
};

// Model Preference Enum (0-2)
export const MODEL_PREFERENCES = {
    0: "gemini-3.8-flash",
    1: "gemini-3.8-flash",
    2: "gemini-3.8-flash"
};

// Reverse mappings for converting text to integers
export const BIRTH_YEAR_RANGE_TO_INT = Object.fromEntries(
    Object.entries(BIRTH_YEAR_RANGES).map(([key, value]) => [value, parseInt(key)])
);

export const EDUCATION_LEVEL_TO_INT = Object.fromEntries(
    Object.entries(EDUCATION_LEVELS).map(([key, value]) => [value, parseInt(key)])
);

export const LANGUAGE_TO_INT = Object.fromEntries(
    Object.entries(LANGUAGES).map(([key, value]) => [value, parseInt(key)])
);

export const LEARNING_PURPOSE_TO_INT = Object.fromEntries(
    Object.entries(LEARNING_PURPOSES).map(([key, value]) => [value, parseInt(key)])
);

export const KNOWLEDGE_STATUS_TO_INT = Object.fromEntries(
    Object.entries(KNOWLEDGE_STATUSES).map(([key, value]) => [value, parseInt(key)])
);

export const ROLE_TO_INT = Object.fromEntries(
    Object.entries(ROLES).map(([key, value]) => [value, parseInt(key)])
);

export const TRISTATE_STATE_TO_INT = Object.fromEntries(
    Object.entries(TRISTATE_STATES).map(([key, value]) => [value, parseInt(key)])
);

export const MODEL_PREFERENCE_TO_INT = {
    'gemini-3.8-flash': 0,
    'gemini-2.5-pro': 0,
    'gemini-2.5-flash': 1,
    'gemini-2.5-flash-lite': 2
};

// Utility functions for converting between text and integers
export const convertToInt = {
    birthYearRange: (text) => BIRTH_YEAR_RANGE_TO_INT[text] ?? null,
    educationLevel: (text) => EDUCATION_LEVEL_TO_INT[text] ?? null,
    language: (text) => LANGUAGE_TO_INT[text] ?? null,
    learningPurpose: (text) => LEARNING_PURPOSE_TO_INT[text] ?? null,
    knowledgeStatus: (text) => KNOWLEDGE_STATUS_TO_INT[text] ?? null,
    role: (text) => ROLE_TO_INT[text] ?? null,
    tristateState: (text) => TRISTATE_STATE_TO_INT[text] ?? 0, // Default to tip-only (0)
    modelPreference: (text) => MODEL_PREFERENCE_TO_INT[text] ?? 0 // Default to gemini-3.8-flash (0)
};

export const convertToText = {
    birthYearRange: (int) => {
        const result = BIRTH_YEAR_RANGES[int] ?? null;
        if (DEBUG_CONVERSIONS) {
            console.log(`convertToText.birthYearRange(${int}) = ${result}`);
        }
        return result;
    },
    educationLevel: (int) => {
        const result = EDUCATION_LEVELS[int] ?? null;
        if (DEBUG_CONVERSIONS) {
            console.log(`convertToText.educationLevel(${int}) = ${result}`);
        }
        return result;
    },
    language: (int) => {
        const result = LANGUAGES[int] ?? null;
        if (DEBUG_CONVERSIONS) {
            console.log(`convertToText.language(${int}) = ${result}`);
        }
        return result;
    },
    learningPurpose: (int) => LEARNING_PURPOSES[int] ?? null,
    knowledgeStatus: (int) => KNOWLEDGE_STATUSES[int] ?? null,
    role: (int) => ROLES[int] ?? null,
    tristateState: (int) => TRISTATE_STATES[int] ?? "tip-only",
    modelPreference: (int) => MODEL_PREFERENCES[int] ?? "gemini-3.8-flash" // Default to gemini-3.8-flash
};

// Helper function to convert language code to full name for prompts
export const getLanguageName = (languageCode) => {
    return LANGUAGE_NAMES[languageCode] || 'English';
};

// Helper function to convert frontend profile data to database format
export const convertProfileToDatabase = (profileData) => {
    return {
        ...profileData,
        name: profileData.name || null,
        birthYearRange: profileData.ageRange !== null && profileData.ageRange !== undefined ? convertToInt.birthYearRange(profileData.ageRange) : null,
        educationLevel: profileData.educationLevel !== null && profileData.educationLevel !== undefined ? convertToInt.educationLevel(profileData.educationLevel) : null,
        uiLanguage: convertToInt.language(profileData.uiLanguage || 'en'),
        preferredModel: profileData.preferredModel !== null && profileData.preferredModel !== undefined ? convertToInt.modelPreference(profileData.preferredModel) : 0,
        last_used_timezone: profileData.lastUsedTimezone || 480,
        // Token usage tracking (these are read-only from frontend, managed by backend)
        total_input_tokens: profileData.totalInputTokens || 0,
        total_output_tokens: profileData.totalOutputTokens || 0,
        total_tips_generated: profileData.totalTipsGenerated || 0,
        total_conversations: profileData.totalConversations || 0
    };
};

// Helper function to convert database profile data to frontend format
export const convertProfileFromDatabase = (profileData) => {
    if (DEBUG_CONVERSIONS) {
        console.log('=== convertProfileFromDatabase START ===');
        console.log('Input profileData:', profileData);
        console.log('profileData.birth_year_range:', profileData.birth_year_range);
        console.log('profileData.education_level:', profileData.education_level);
        console.log('profileData.ui_language:', profileData.ui_language);
    }
    
    const convertedAgeRange = convertToText.birthYearRange(profileData.birth_year_range);
    const convertedEducationLevel = convertToText.educationLevel(profileData.education_level);
    const convertedUILanguage = convertToText.language(profileData.ui_language);
    
    if (DEBUG_CONVERSIONS) {
        console.log('Converted ageRange:', convertedAgeRange);
        console.log('Converted educationLevel:', convertedEducationLevel);
        console.log('Converted uiLanguage:', convertedUILanguage);
    }
    
    const result = {
        ...profileData,
        name: profileData.name,
        ageRange: convertedAgeRange,
        educationLevel: convertedEducationLevel,
        uiLanguage: convertedUILanguage,
        preferredModel: convertToText.modelPreference(profileData.preferred_model),
        hideSlowChatNotification: profileData.hide_slow_chat_notification || false,
        lastUsedTimezone: profileData.last_used_timezone || 480,
        // Token usage tracking
        totalInputTokens: profileData.total_input_tokens || 0,
        totalOutputTokens: profileData.total_output_tokens || 0,
        totalTipsGenerated: profileData.total_tips_generated || 0,
        totalConversations: profileData.total_conversations || 0
    };
    
    if (DEBUG_CONVERSIONS) {
        console.log('Final converted result:', result);
        console.log('=== convertProfileFromDatabase END ===');
    }
    
    return result;
};

// Helper function to convert frontend topic data to database format
export const convertTopicToDatabase = (topicData) => {
    // Convert frequency to hours for database storage
    const frequencyInHours = convertFrequencyToHours(
        topicData.scheduleFrequencyValue || 1,
        (topicData.scheduleFrequencyUnit !== undefined && topicData.scheduleFrequencyUnit !== null) 
            ? topicData.scheduleFrequencyUnit 
            : 1 // Default to days
    );
    
    return {
        ...topicData,
        learningPurpose: convertToInt.learningPurpose(topicData.learningPurpose),
        contentLanguage: convertToInt.language(topicData.contentLanguage) ?? 0, // Default to "en" (0) if null/undefined
        // Convert camelCase to snake_case for database
        is_favorite: topicData.isFavorite || false,
        is_scheduled: topicData.isScheduled || false,
        is_generating_tip: topicData.isGeneratingTip || false,
        is_generating_response: topicData.isGeneratingResponse || false,
        is_generating_summary: topicData.isGeneratingSummary || false,
        schedule_frequency_hours: frequencyInHours,
        // Use explicit check to preserve 0 (12 AM)
        schedule_time_hour: (topicData.scheduleTimeHour !== undefined && topicData.scheduleTimeHour !== null) 
            ? topicData.scheduleTimeHour 
            : 7,
        // Use explicit check to preserve 0 (AM)
        schedule_time_period: (topicData.scheduleTimePeriod !== undefined && topicData.scheduleTimePeriod !== null) 
            ? topicData.scheduleTimePeriod 
            : 0, // Default to AM
        schedule_frequency_value: topicData.scheduleFrequencyValue || 1,
        // Use explicit check to preserve 0 (hours)
        schedule_frequency_unit: (topicData.scheduleFrequencyUnit !== undefined && topicData.scheduleFrequencyUnit !== null) 
            ? topicData.scheduleFrequencyUnit 
            : 1, // Default to days
        user_timezone: topicData.userTimezone || TIMEZONE_ENUM.LOS_ANGELES, // Default to Los Angeles
        scheduled_skip_if_unread: topicData.scheduledSkipIfUnread !== undefined ? topicData.scheduledSkipIfUnread : false,
        learning_summary: topicData.learningSummary || '',
        include_summary_in_context: topicData.includeSummaryInContext !== undefined ? topicData.includeSummaryInContext : true,
        group_id: topicData.groupId || null
    };
};

// Helper function to convert database topic data to frontend format
export const convertTopicFromDatabase = (topicData) => {
    return {
        ...topicData,
        knowledgeLevel: topicData.knowledge_level !== null ? Number(topicData.knowledge_level) : null,
        learningPurpose: convertToText.learningPurpose(topicData.learning_purpose),
        contentLanguage: convertToText.language(topicData.content_language),
        // Convert snake_case to camelCase for frontend
        isFavorite: topicData.is_favorite || false,
        isScheduled: topicData.is_scheduled || false,
        isGeneratingTip: topicData.is_generating_tip || false,
        isGeneratingResponse: topicData.is_generating_response || false,
        isGeneratingTopic: topicData.is_generating_topic || false,
        isGeneratingSummary: topicData.is_generating_summary || false,
        scheduleFrequencyHours: topicData.schedule_frequency_hours || 24,
        displayOrder: topicData.display_order || null,
        // Use explicit check to preserve 0 (12 AM)
        scheduleTimeHour: (topicData.schedule_time_hour !== undefined && topicData.schedule_time_hour !== null) 
            ? topicData.schedule_time_hour 
            : 7,
        // Use explicit check to preserve 0 (AM)
        scheduleTimePeriod: (topicData.schedule_time_period !== undefined && topicData.schedule_time_period !== null) 
            ? topicData.schedule_time_period 
            : 0, // Default to AM
        scheduleFrequencyValue: topicData.schedule_frequency_value || 1,
        // Use explicit check to preserve 0 (hours)
        scheduleFrequencyUnit: (topicData.schedule_frequency_unit !== undefined && topicData.schedule_frequency_unit !== null) 
            ? topicData.schedule_frequency_unit 
            : 1, // Default to days
        userTimezone: topicData.user_timezone || TIMEZONE_ENUM.LOS_ANGELES, // Default to Los Angeles
        lastScheduledTipAt: topicData.last_scheduled_tip_at || null,
        nextDueTime: topicData.next_due_time || null,
        scheduledSkipIfUnread: topicData.scheduled_skip_if_unread === true || topicData.scheduled_skip_if_unread === 1,
        learningSummary: topicData.learning_summary || '',
        includeSummaryInContext: topicData.include_summary_in_context !== undefined ? topicData.include_summary_in_context : true,
        groupId: topicData.group_id || null
    };
};

// Helper function to convert frontend tip data to database format
export const convertTipToDatabase = (tipData) => {
    return {
        ...tipData,
        knowledgeStatus: tipData.knowledgeStatus ? convertToInt.knowledgeStatus(tipData.knowledgeStatus) : null,
        tristateState: convertToInt.tristateState(tipData.tristateState),
        // Convert camelCase to snake_case for database
        is_hidden: tipData.isHidden || false,
        is_favorite: tipData.isFavorite || false,
        is_scheduled_for_review: tipData.isScheduledForReview || false,
        review_is_due: tipData.reviewIsDue || false,
        next_review_due_date: tipData.nextReviewDueDate || null,
        review_count: tipData.reviewCount || 0,
        // Token usage tracking
        input_tokens: tipData.inputTokens || 0,
        output_tokens: tipData.outputTokens || 0,
        conversation_count: tipData.conversationCount || 0
    };
};

// Helper function to convert database tip data to frontend format
export const convertTipFromDatabase = (tipData) => {
    if (DEBUG_CONVERSIONS) {
        console.log('=== convertTipFromDatabase START ===');
        console.log('Input tipData:', tipData);
    }
    
    let createdAt = tipData.created_at || tipData.createdAt;
    if (!createdAt || createdAt === 'invalid' || isNaN(new Date(createdAt).getTime())) {
        console.warn('Invalid or missing createdAt in tipData:', createdAt, tipData);
    }
    
    const result = {
        ...tipData,
        topicId: tipData.topic_id,
        knowledgeStatus: tipData.knowledge_status !== null ? convertToText.knowledgeStatus(tipData.knowledge_status) : null,
        tristateState: convertToText.tristateState(tipData.tristate_state),
        // Convert snake_case to camelCase for frontend
        isHidden: tipData.is_hidden || false,
        isFavorite: tipData.is_favorite || false,
        createdAt: createdAt,
        dateLearned: tipData.date_learned || null,
        isScheduledForReview: tipData.is_scheduled_for_review || false,
        reviewIsDue: tipData.review_is_due || false,
        nextReviewDueDate: tipData.next_review_due_date || null,
        reviewCount: tipData.review_count || 0,
        hasReviewQuestions: tipData.has_review_questions || false,
        // Token usage tracking
        inputTokens: tipData.input_tokens || 0,
        outputTokens: tipData.output_tokens || 0,
        conversationCount: tipData.conversation_count || 0
    };
    
    if (DEBUG_CONVERSIONS) {
        console.log('Output tip (frontend format):', result);
        console.log('=== convertTipFromDatabase END ===');
    }
    
    return result;
};

// Helper function to convert frontend conversation data to database format
export const convertConversationToDatabase = (conversationData) => {
    return {
        ...conversationData,
        role: convertToInt.role(conversationData.role)
    };
};

// Helper function to convert database conversation data to frontend format
export const convertConversationFromDatabase = (conversationData) => {
    return {
        ...conversationData,
        tipId: conversationData.tip_id,
        text: conversationData.message,
        role: convertToText.role(conversationData.role),
        isPendingResponse: conversationData.is_pending_response
    };
};

// Helper function to convert frequency value and unit to hours
export const convertFrequencyToHours = (value, unit) => {
    // In production, don't allow hours (unit 0)
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'production' && unit === 0) {
        console.warn('Hours frequency not allowed in production, defaulting to 1 day');
        return 24; // Default to 1 day
    }
    
    switch (unit) {
        case 0: // hours
            return value;
        case 1: // days
            return value * 24;
        case 2: // weeks
            return value * 24 * 7;
        case 3: // months (approximate as 30 days)
            return value * 24 * 30;
        default:
            return value * 24; // Default to days
    }
};

// Helper function to get timezone offset in minutes from browser
export const getTimezoneOffset = () => {
    return new Date().getTimezoneOffset();
};

// Timezone enum with major cities and their UTC offsets in minutes
export const TIMEZONE_ENUM = {
    // UTC-12 to UTC+14 (ordered by offset, one city per timezone)
    HONOLULU: 600,        // UTC-10 (HST)
    ANCHORAGE: 540,       // UTC-9 (AKST/AKDT)
    LOS_ANGELES: 480,     // UTC-8 (PST/PDT)
    DENVER: 420,          // UTC-7 (MST/MDT)
    CHICAGO: 360,         // UTC-6 (CST/CDT)
    NEW_YORK: 300,        // UTC-5 (EST/EDT)
    LIMA: 300,            // UTC-5 (PET) - Same as New York
    SAO_PAULO: 180,       // UTC-3 (BRT/BRST)
    BUENOS_AIRES: 180,    // UTC-3 (ART) - Same as Sao Paulo
    AZORES: 60,           // UTC-1 (AZOT/AZOST)
    LONDON: 0,            // UTC+0 (GMT/BST)
    REYKJAVIK: 0,         // UTC+0 (GMT) - Same as London
    PARIS: -60,           // UTC+1 (CET/CEST)
    LAGOS: -60,           // UTC+1 (WAT) - Same as Paris
    CAIRO: -120,          // UTC+2 (EET/EEST)
    JOHANNESBURG: -120,   // UTC+2 (SAST) - Same as Cairo
    MOSCOW: -180,         // UTC+3 (MSK)
    ISTANBUL: -180,       // UTC+3 (TRT) - Same as Moscow
    DUBAI: -240,          // UTC+4 (GST)
    NEW_DELHI: -330,      // UTC+5:30 (IST)
    BANGKOK: -420,        // UTC+7 (ICT)
    JAKARTA: -420,        // UTC+7 (WIB) - Same as Bangkok
    BEIJING: -480,        // UTC+8 (CST)
    HONG_KONG: -480,      // UTC+8 (HKT) - Same as Beijing
    SINGAPORE: -480,      // UTC+8 (SGT) - Same as Beijing
    TOKYO: -540,          // UTC+9 (JST)
    SEOUL: -540,          // UTC+9 (KST) - Same as Tokyo
    SYDNEY: -600,         // UTC+10 (AEST/AEDT)
    MELBOURNE: -600,      // UTC+10 (AEST/AEDT) - Same as Sydney
    GUAM: -600,           // UTC+10 (ChST) - Same as Sydney
    AUCKLAND: -720,       // UTC+12 (NZST/NZDT)
    FIJI: -720,           // UTC+12 (FJT) - Same as Auckland
};

// Timezone display names with multi-language support and UTC offsets
export const TIMEZONE_NAMES = {
    [TIMEZONE_ENUM.HONOLULU]: {
        en: 'Honolulu (UTC-10)',
        'zh-CN': '檀香山 (UTC-10)',
        'zh-Hant': '檀香山 (UTC-10)',
        ja: 'ホノルル (UTC-10)'
    },
    [TIMEZONE_ENUM.ANCHORAGE]: {
        en: 'Anchorage (UTC-9)',
        'zh-CN': '安克雷奇 (UTC-9)',
        'zh-Hant': '安克拉治 (UTC-9)',
        ja: 'アンカレッジ (UTC-9)'
    },
    [TIMEZONE_ENUM.LOS_ANGELES]: {
        en: 'Los Angeles (UTC-8)',
        'zh-CN': '洛杉矶 (UTC-8)',
        'zh-Hant': '洛杉磯 (UTC-8)',
        ja: 'ロサンゼルス (UTC-8)'
    },
    [TIMEZONE_ENUM.DENVER]: {
        en: 'Denver (UTC-7)',
        'zh-CN': '丹佛 (UTC-7)',
        'zh-Hant': '丹佛 (UTC-7)',
        ja: 'デンバー (UTC-7)'
    },
    [TIMEZONE_ENUM.CHICAGO]: {
        en: 'Chicago (UTC-6)',
        'zh-CN': '芝加哥 (UTC-6)',
        'zh-Hant': '芝加哥 (UTC-6)',
        ja: 'シカゴ (UTC-6)'
    },
    [TIMEZONE_ENUM.NEW_YORK]: {
        en: 'New York (UTC-5)',
        'zh-CN': '纽约 (UTC-5)',
        'zh-Hant': '紐約 (UTC-5)',
        ja: 'ニューヨーク (UTC-5)'
    },
    [TIMEZONE_ENUM.LIMA]: {
        en: 'Lima (UTC-5)',
        'zh-CN': '利马 (UTC-5)',
        'zh-Hant': '利馬 (UTC-5)',
        ja: 'リマ (UTC-5)'
    },
    [TIMEZONE_ENUM.SAO_PAULO]: {
        en: 'São Paulo (UTC-3)',
        'zh-CN': '圣保罗 (UTC-3)',
        'zh-Hant': '聖保羅 (UTC-3)',
        ja: 'サンパウロ (UTC-3)'
    },
    [TIMEZONE_ENUM.BUENOS_AIRES]: {
        en: 'Buenos Aires (UTC-3)',
        'zh-CN': '布宜诺斯艾利斯 (UTC-3)',
        'zh-Hant': '布宜諾斯艾利斯 (UTC-3)',
        ja: 'ブエノスアイレス (UTC-3)'
    },
    [TIMEZONE_ENUM.AZORES]: {
        en: 'Azores (UTC-1)',
        'zh-CN': '亚速尔群岛 (UTC-1)',
        'zh-Hant': '亞速群島 (UTC-1)',
        ja: 'アゾレス諸島 (UTC-1)'
    },
    [TIMEZONE_ENUM.LONDON]: {
        en: 'London (UTC+0)',
        'zh-CN': '伦敦 (UTC+0)',
        'zh-Hant': '倫敦 (UTC+0)',
        ja: 'ロンドン (UTC+0)'
    },
    [TIMEZONE_ENUM.REYKJAVIK]: {
        en: 'Reykjavik (UTC+0)',
        'zh-CN': '雷克雅未克 (UTC+0)',
        'zh-Hant': '雷克雅維克 (UTC+0)',
        ja: 'レイキャビク (UTC+0)'
    },
    [TIMEZONE_ENUM.PARIS]: {
        en: 'Paris (UTC+1)',
        'zh-CN': '巴黎 (UTC+1)',
        'zh-Hant': '巴黎 (UTC+1)',
        ja: 'パリ (UTC+1)'
    },
    [TIMEZONE_ENUM.LAGOS]: {
        en: 'Lagos (UTC+1)',
        'zh-CN': '拉各斯 (UTC+1)',
        'zh-Hant': '拉哥斯 (UTC+1)',
        ja: 'ラゴス (UTC+1)'
    },
    [TIMEZONE_ENUM.CAIRO]: {
        en: 'Cairo (UTC+2)',
        'zh-CN': '开罗 (UTC+2)',
        'zh-Hant': '開羅 (UTC+2)',
        ja: 'カイロ (UTC+2)'
    },
    [TIMEZONE_ENUM.JOHANNESBURG]: {
        en: 'Johannesburg (UTC+2)',
        'zh-CN': '约翰内斯堡 (UTC+2)',
        'zh-Hant': '約翰尼斯堡 (UTC+2)',
        ja: 'ヨハネスブルグ (UTC+2)'
    },
    [TIMEZONE_ENUM.MOSCOW]: {
        en: 'Moscow (UTC+3)',
        'zh-CN': '莫斯科 (UTC+3)',
        'zh-Hant': '莫斯科 (UTC+3)',
        ja: 'モスクワ (UTC+3)'
    },
    [TIMEZONE_ENUM.ISTANBUL]: {
        en: 'Istanbul (UTC+3)',
        'zh-CN': '伊斯坦布尔 (UTC+3)',
        'zh-Hant': '伊斯坦堡 (UTC+3)',
        ja: 'イスタンブール (UTC+3)'
    },
    [TIMEZONE_ENUM.DUBAI]: {
        en: 'Dubai (UTC+4)',
        'zh-CN': '迪拜 (UTC+4)',
        'zh-Hant': '杜拜 (UTC+4)',
        ja: 'ドバイ (UTC+4)'
    },
    [TIMEZONE_ENUM.NEW_DELHI]: {
        en: 'New Delhi (UTC+5:30)',
        'zh-CN': '新德里 (UTC+5:30)',
        'zh-Hant': '新德里 (UTC+5:30)',
        ja: 'ニューデリー (UTC+5:30)'
    },
    [TIMEZONE_ENUM.BANGKOK]: {
        en: 'Bangkok (UTC+7)',
        'zh-CN': '曼谷 (UTC+7)',
        'zh-Hant': '曼谷 (UTC+7)',
        ja: 'バンコク (UTC+7)'
    },
    [TIMEZONE_ENUM.JAKARTA]: {
        en: 'Jakarta (UTC+7)',
        'zh-CN': '雅加达 (UTC+7)',
        'zh-Hant': '雅加達 (UTC+7)',
        ja: 'ジャカルタ (UTC+7)'
    },
    [TIMEZONE_ENUM.BEIJING]: {
        en: 'Beijing (UTC+8)',
        'zh-CN': '北京 (UTC+8)',
        'zh-Hant': '北京 (UTC+8)',
        ja: '北京 (UTC+8)'
    },
    [TIMEZONE_ENUM.HONG_KONG]: {
        en: 'Hong Kong (UTC+8)',
        'zh-CN': '香港 (UTC+8)',
        'zh-Hant': '香港 (UTC+8)',
        ja: '香港 (UTC+8)'
    },
    [TIMEZONE_ENUM.SINGAPORE]: {
        en: 'Singapore (UTC+8)',
        'zh-CN': '新加坡 (UTC+8)',
        'zh-Hant': '新加坡 (UTC+8)',
        ja: 'シンガポール (UTC+8)'
    },
    [TIMEZONE_ENUM.TOKYO]: {
        en: 'Tokyo (UTC+9)',
        'zh-CN': '东京 (UTC+9)',
        'zh-Hant': '東京 (UTC+9)',
        ja: '東京 (UTC+9)'
    },
    [TIMEZONE_ENUM.SEOUL]: {
        en: 'Seoul (UTC+9)',
        'zh-CN': '首尔 (UTC+9)',
        'zh-Hant': '首爾 (UTC+9)',
        ja: 'ソウル (UTC+9)'
    },
    [TIMEZONE_ENUM.SYDNEY]: {
        en: 'Sydney (UTC+10)',
        'zh-CN': '悉尼 (UTC+10)',
        'zh-Hant': '雪梨 (UTC+10)',
        ja: 'シドニー (UTC+10)'
    },
    [TIMEZONE_ENUM.MELBOURNE]: {
        en: 'Melbourne (UTC+10)',
        'zh-CN': '墨尔本 (UTC+10)',
        'zh-Hant': '墨爾本 (UTC+10)',
        ja: 'メルボルン (UTC+10)'
    },
    [TIMEZONE_ENUM.GUAM]: {
        en: 'Guam (UTC+10)',
        'zh-CN': '关岛 (UTC+10)',
        'zh-Hant': '關島 (UTC+10)',
        ja: 'グアム (UTC+10)'
    },
    [TIMEZONE_ENUM.AUCKLAND]: {
        en: 'Auckland (UTC+12)',
        'zh-CN': '奥克兰 (UTC+12)',
        'zh-Hant': '奧克蘭 (UTC+12)',
        ja: 'オークランド (UTC+12)'
    },
    [TIMEZONE_ENUM.FIJI]: {
        en: 'Fiji (UTC+12)',
        'zh-CN': '斐济 (UTC+12)',
        'zh-Hant': '斐濟 (UTC+12)',
        ja: 'フィジー (UTC+12)'
    },
};

// Helper function to get timezone name by language
export const getTimezoneName = (timezoneEnum, language = 'en') => {
    return TIMEZONE_NAMES[timezoneEnum]?.[language] || TIMEZONE_NAMES[timezoneEnum]?.en || 'Unknown';
};

// Helper function to get timezone enum from offset
export const getTimezoneEnumFromOffset = (offsetMinutes) => {
    // Find the timezone enum that matches the offset
    for (const [, value] of Object.entries(TIMEZONE_ENUM)) {
        if (value === offsetMinutes) {
            return value; // Return the enum value (offset)
        }
    }
    
    // If no exact match, try to find the closest match for common offsets
    const commonOffsets = {
        600: TIMEZONE_ENUM.HONOLULU,     // UTC-10 (HST)
        540: TIMEZONE_ENUM.ANCHORAGE,    // UTC-9 (AKST/AKDT)
        480: TIMEZONE_ENUM.LOS_ANGELES,  // UTC-8 (PST/PDT)
        420: TIMEZONE_ENUM.DENVER,       // UTC-7 (MST/MDT)
        360: TIMEZONE_ENUM.CHICAGO,      // UTC-6 (CST/CDT)
        300: TIMEZONE_ENUM.NEW_YORK,     // UTC-5 (EST/EDT)
        180: TIMEZONE_ENUM.SAO_PAULO,    // UTC-3 (BRT/BRST)
        60: TIMEZONE_ENUM.AZORES,        // UTC-1 (AZOT/AZOST)
        0: TIMEZONE_ENUM.LONDON,         // UTC+0 (GMT/BST)
        '-60': TIMEZONE_ENUM.PARIS,      // UTC+1 (CET/CEST)
        '-120': TIMEZONE_ENUM.CAIRO,     // UTC+2 (EET/EEST)
        '-180': TIMEZONE_ENUM.MOSCOW,    // UTC+3 (MSK)
        '-240': TIMEZONE_ENUM.DUBAI,     // UTC+4 (GST)
        '-330': TIMEZONE_ENUM.NEW_DELHI, // UTC+5:30 (IST)
        '-420': TIMEZONE_ENUM.BANGKOK,   // UTC+7 (ICT)
        '-480': TIMEZONE_ENUM.BEIJING,   // UTC+8 (CST)
        '-540': TIMEZONE_ENUM.TOKYO,     // UTC+9 (JST)
        '-600': TIMEZONE_ENUM.SYDNEY,    // UTC+10 (AEST/AEDT)
        '-720': TIMEZONE_ENUM.AUCKLAND,  // UTC+12 (NZST/NZDT)
    };
    
    if (commonOffsets[offsetMinutes]) {
        return commonOffsets[offsetMinutes];
    }
    
    // Default to Los Angeles if no match found
    return TIMEZONE_ENUM.LOS_ANGELES;
};

// Helper function to get timezone enum from browser timezone
export const getTimezoneEnumFromBrowser = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const timezoneString = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Try to map based on timezone string first (more accurate)
    const timezoneMapping = {
        'America/Los_Angeles': TIMEZONE_ENUM.LOS_ANGELES,
        'America/Seattle': TIMEZONE_ENUM.LOS_ANGELES, // Seattle uses same offset as LA
        'America/Denver': TIMEZONE_ENUM.DENVER,
        'America/Chicago': TIMEZONE_ENUM.CHICAGO,
        'America/New_York': TIMEZONE_ENUM.NEW_YORK,
        'America/Anchorage': TIMEZONE_ENUM.ANCHORAGE,
        'Pacific/Honolulu': TIMEZONE_ENUM.HONOLULU,
        'Europe/London': TIMEZONE_ENUM.LONDON,
        'Europe/Paris': TIMEZONE_ENUM.PARIS,
        'Europe/Moscow': TIMEZONE_ENUM.MOSCOW,
        'Europe/Istanbul': TIMEZONE_ENUM.ISTANBUL,
        'Asia/Tokyo': TIMEZONE_ENUM.TOKYO,
        'Asia/Seoul': TIMEZONE_ENUM.SEOUL,
        'Asia/Shanghai': TIMEZONE_ENUM.BEIJING, // Map to Beijing since Shanghai was removed
        'Asia/Hong_Kong': TIMEZONE_ENUM.HONG_KONG,
        'Asia/Singapore': TIMEZONE_ENUM.SINGAPORE,
        'Asia/Bangkok': TIMEZONE_ENUM.BANGKOK,
        'Asia/Jakarta': TIMEZONE_ENUM.JAKARTA,
        'Asia/Kolkata': TIMEZONE_ENUM.NEW_DELHI,
        'Asia/Dubai': TIMEZONE_ENUM.DUBAI,
        'Australia/Sydney': TIMEZONE_ENUM.SYDNEY,
        'Australia/Melbourne': TIMEZONE_ENUM.MELBOURNE,
        'Pacific/Auckland': TIMEZONE_ENUM.AUCKLAND,
        'America/Sao_Paulo': TIMEZONE_ENUM.SAO_PAULO,
        'America/Argentina/Buenos_Aires': TIMEZONE_ENUM.BUENOS_AIRES,
        'America/Lima': TIMEZONE_ENUM.LIMA,
        'Africa/Cairo': TIMEZONE_ENUM.CAIRO,
        'Africa/Johannesburg': TIMEZONE_ENUM.JOHANNESBURG,
        'Africa/Lagos': TIMEZONE_ENUM.LAGOS,
        'Pacific/Fiji': TIMEZONE_ENUM.FIJI,
        'Pacific/Guam': TIMEZONE_ENUM.GUAM,
        'Atlantic/Azores': TIMEZONE_ENUM.AZORES,
        'Atlantic/Reykjavik': TIMEZONE_ENUM.REYKJAVIK,
    };
    
    if (timezoneMapping[timezoneString]) {
        return timezoneMapping[timezoneString];
    }
    
    // Fallback to offset-based detection
    const result = getTimezoneEnumFromOffset(offset);
    return result;
};

// Helper function to get all timezone options for dropdown
export const getTimezoneOptions = (language = 'en') => {
    // Create a Map to deduplicate by value (offset)
    const uniqueTimezones = new Map();
    
    Object.entries(TIMEZONE_ENUM).forEach(([, value]) => {
        if (!uniqueTimezones.has(value)) {
            uniqueTimezones.set(value, {
                value: value,
                label: getTimezoneName(value, language)
            });
        }
    });
    
    return Array.from(uniqueTimezones.values())
        .sort((a, b) => a.value - b.value); // Sort by offset
}; 